 
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './index.css';
import { preserveSeoContent } from './utils/seoShell';

// Check if this is an embed route BEFORE loading heavy dependencies
// This provides instant loading for embeds without auth, analytics, etc.
const isEmbedRoute = window.location.pathname.startsWith('/embed/');

if (isEmbedRoute) {
  // Lightweight embed app - no auth, no analytics, no player context
  import('./EmbedApp').then(({ EmbedApp }) => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <EmbedApp />
      </StrictMode>
    );
  });
} else {
  // Full app with all providers and analytics
  initFullApp();
}

async function initFullApp() {
  // Dynamic imports for code splitting
  const [
    Sentry,
    { supabaseIntegration },
    { SupabaseClient },
    { default: App },
    { PostHogWrapper },
    { initWebVitals },
    { hasAnalyticsConsent },
  ] = await Promise.all([
    import('@sentry/react'),
    import('@supabase/sentry-js-integration'),
    import('@supabase/supabase-js'),
    import('./App.tsx'),
    import('./components/PostHogWrapper'),
    import('./lib/webVitals'),
    import('./utils/cookieConsent'),
  ]);

  // Clean up _refresh cache-busting param from URL (added by usePageVisibility hard reload)
  if (window.location.search.includes('_refresh=')) {
    const url = new URL(window.location.href);
    url.searchParams.delete('_refresh');
    window.history.replaceState(null, '', url.toString());
  }

  // Check analytics consent and environment
  const analyticsConsent = hasAnalyticsConsent();
  const isDevelopment = import.meta.env.MODE === 'development';
  const shouldEnablePostHog = !isDevelopment;

  // Initialize Sentry for error tracking (strictly necessary for site reliability - GDPR legitimate interest)
  if (import.meta.env.VITE_SENTRY_DSN && !isDevelopment) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      // Sentry v11 collects user info, cookies, HTTP bodies and DB query data by default.
      // This init runs without consent (legitimate interest), so keep v10's minimal collection.
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpBodies: [],
        databaseQueryData: false,
      },
      // v11 defaults this to true, which marks sessions errored on every logger.warn (captureMessage)
      attachStacktrace: false,
      integrations: [
        supabaseIntegration(SupabaseClient, Sentry, {
          tracing: true,
          breadcrumbs: true,
          errors: true,
        }),
        Sentry.browserTracingIntegration({
          shouldCreateSpanForRequest: (url) => {
            return !url.startsWith(`${import.meta.env.VITE_SUPABASE_URL}/rest`);
          },
        }),
      ],
      tracesSampleRate: 0.1,
      beforeSend(event) {
        const errorMessage = event.exception?.values?.[0]?.value || event.message || '';
        const errorType = event.exception?.values?.[0]?.type || '';
        const stacktrace = event.exception?.values?.[0]?.stacktrace?.frames || [];

        // Filter out local development errors
        if (errorMessage.includes('127.0.0.1') ||
            errorMessage.includes('localhost') ||
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1') {
          return null;
        }

        // Filter out PostHog errors
        const isPostHogError =
          errorMessage.includes('posthog') ||
          errorMessage.includes('PostHog') ||
          errorMessage.includes('session recording') ||
          stacktrace.some(frame =>
            frame.filename?.includes('posthog') ||
            frame.filename?.includes('PostHog')
          );
        if (isPostHogError) return null;

        // Filter out autoplay blocked errors
        if (errorMessage.includes('autoplay') ||
            errorMessage.includes('Autoplay blocked') ||
            errorMessage.includes('user interaction required')) {
          return null;
        }

        // Filter out realtime channel errors
        const extraStatus = (event.extra as Record<string, unknown>)?.status;
        if (errorMessage.includes('Realtime channel error') ||
            errorMessage.includes('CHANNEL_ERROR') ||
            errorMessage.includes('subscription error') ||
            extraStatus === 'CHANNEL_ERROR') {
          return null;
        }

        // Sample network errors at 10%
        const isNetworkError =
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('Network request failed') ||
          errorMessage.includes('Failed to send a request to the Edge Function') ||
          errorType === 'FunctionsFetchError' ||
          errorMessage.includes('Error fetching') ||
          errorMessage.includes('Load failed');
        if (isNetworkError && Math.random() > 0.1) return null;

        // Filter out stale chunk errors
        if (errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('dynamically imported')) {
          return null;
        }

        // Filter out Facebook WebView errors
        if (errorMessage.includes('Java object is gone') ||
            errorMessage.includes('postMessage')) {
          return null;
        }

        // Filter out media playback errors
        if (errorMessage.includes('MEDIA_ERR_SRC_NOT_SUPPORTED') ||
            errorMessage.includes('Audio playback error')) {
          return null;
        }

        // Filter out AbortError
        if (errorType === 'AbortError' ||
            errorMessage.includes('signal is aborted')) {
          return null;
        }

        // Filter out expected auth errors
        if (errorType === 'AuthApiError' &&
            (errorMessage.includes('Invalid login credentials') ||
             errorMessage.includes('Email not confirmed'))) {
          return null;
        }

        // Filter out boost validation errors
        if (errorMessage.includes('already have an active or pending boost')) {
          return null;
        }

        return event;
      },
    });
  }

  // Initialize analytics (only in production, respects consent)
  if (analyticsConsent) {
    initWebVitals();
  }

  const AppWithProviders = () => (
    <PostHogWrapper enabled={shouldEnablePostHog} analyticsConsent={analyticsConsent}>
      <App />
    </PostHogWrapper>
  );

  const rootElement = document.getElementById('root')!;
  // createRoot clears #root, so move the worker's crawlable content out of it first
  preserveSeoContent(rootElement);
  createRoot(rootElement).render(
    <StrictMode>
      <AppWithProviders />
    </StrictMode>
  );
}
